import { skipToken } from '@apollo/client/react'
import { CircularProgress } from '@mui/material'
import { eachOfLimit } from 'async'
import { CanceledError } from 'axios'
import type { MutableRefObject, ReactNode } from 'react'
import { useCallback, useContext } from 'react'
import type { DropzoneOptions } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcBaselineCancel from '~icons/ic/baseline-cancel'
import IcOutlinePhotoLibrary from '~icons/ic/outline-photo-library'
import IcRoundPlus from '~icons/ic/round-plus'
import { updateGetMedia, useGetMedia } from 'src/services/apollo/media'
import { cx } from 'src/utils/cx'
import { getPublicUrl, getThumbnailUrl } from 'src/utils/media'
import { getRandomSeed } from 'src/utils/rand'
import { uploadMedia } from 'src/utils/upload'
import { useObjectUrl } from 'src/utils/useObjectURL'
import {
  type ImageItemListGroup,
  MediaSelectPreviewPanelItemsProgressContext,
  MediaSelectPreviewPanelItemsRefContext,
  useImageItemList,
  useImageItemListIsNotEmpty,
} from './context'

type FileRecord = { file: File; controller: AbortController }

const acceptedFileTypes = {
  'image/png': [],
  'image/jpeg': [],
  'image/webp': [],
}

const DEFAULT_MAX_ALLOWED = 100

type MediaSelectPreviewPanelProps = {
  group?: ImageItemListGroup
  onFileDialogOpen?: () => void
  onItemUploadSuccess?: (mediaId: string) => void
  onUploadSuccess?: () => void
  uploadTrigger: ({ open }: { open: () => void }) => ReactNode
  maxAllowedMedia?: number
  wrapperProps?: StyleProps
  dropzoneOpts?: DropzoneOptions
  /** Ref to expose the dropzone open() function to parent */
  openRef?: MutableRefObject<(() => void) | undefined>
  /** Ref to expose the onDrop handler to parent (for external drop zones) */
  onDropRef?: MutableRefObject<((files: File[]) => void) | undefined>
}

export function MediaSelectPreviewAddBtn(props: MediaSelectPreviewPanelProps) {
  const { t } = useTranslation(['common'])

  const [, setUploadProgress] = useContext(
    MediaSelectPreviewPanelItemsProgressContext,
  )
  const hasAddedImage = useImageItemListIsNotEmpty(props.group)
  const [imageItemList, setImageItemList] = useImageItemList(props.group)
  const uploadItemsRef = useContext(MediaSelectPreviewPanelItemsRefContext)

  // queue upload requests with size of 3
  const uploadMultiFiles = async (files: Record<string, FileRecord>) => {
    const entries = Object.entries(files)
    entries.forEach((entry: [string, FileRecord]) => {
      const [id, { file, controller }] = entry
      const uploadId = `upload-${id}`
      uploadItemsRef.current?.set(uploadId, { file, controller })
      setImageItemList(prev => [...prev, uploadId])
    })

    await eachOfLimit(entries, 3, async (entry: [string, FileRecord]) => {
      const [id, { file, controller }] = entry
      const uploadId = `upload-${id}`
      try {
        const res = await uploadMedia(file, {
          signal: controller.signal,
          onProgress: progress => {
            setUploadProgress(prev => ({ ...prev, [uploadId]: progress }))
          },
        })
        if (res.media) {
          // Mutate media manually
          updateGetMedia(res.media.id, res.media)
          setImageItemList(prev => {
            const arr = [...prev]
            const idx = arr.findIndex(m => m === uploadId)
            if (idx > -1) {
              arr.splice(idx, 1, res.media!.id)
            }
            return arr
          })
          uploadItemsRef.current?.delete(uploadId)
          props.onItemUploadSuccess?.(res.media.id)
        }
      } catch (err: any) {
        if (!(err instanceof CanceledError)) toast.error(err.message)
        uploadItemsRef.current?.delete(uploadId)
      } finally {
        // Cleanup upload progress
        setUploadProgress(prev => {
          const obj = { ...prev }
          delete obj[uploadId]
          return obj
        })
      }
    })
    props.onUploadSuccess?.()
  }

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (
        imageItemList.length + acceptedFiles.length >
        (props.maxAllowedMedia ?? DEFAULT_MAX_ALLOWED)
      ) {
        return toast.error(
          t('common:media-upload.max-number-exceeded', {
            number: props.maxAllowedMedia ?? DEFAULT_MAX_ALLOWED,
          }),
        )
      }
      const newFiles = Object.fromEntries(
        acceptedFiles.map(file => [
          getRandomSeed(),
          { file, controller: new AbortController() },
        ]),
      )

      uploadMultiFiles(newFiles)
    },
    [imageItemList.length],
  )

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    onFileDialogOpen: () => {
      props.onFileDialogOpen?.()
    },
    accept: acceptedFileTypes,
    multiple: props.maxAllowedMedia === 1 ? false : true,
    ...props.dropzoneOpts,
  })

  if (props.openRef) {
    props.openRef.current = open
  }
  if (props.onDropRef) {
    props.onDropRef.current = onDrop
  }

  return (
    <div
      className={cx(
        'font-medium cursor-pointer flex flex-col gap-3 items-center justify-center text-xs',
        props.wrapperProps?.className,
      )}
      {...getRootProps()}
    >
      <input {...getInputProps()}></input>
      {hasAddedImage ? (
        <>
          <IcRoundPlus className="size-6" />
          <p className="mt-1">{t('market:train-lora.add-image.label')}</p>
        </>
      ) : (
        <>
          <IcOutlinePhotoLibrary className="size-[104px] text-neutral-400" />
          {props.uploadTrigger({ open })}
        </>
      )}
    </div>
  )
}

type ImageItemProps = {
  itemId: string
  isUploading: boolean
  onRemove: (itemId: string) => void
  wrapperProps?: StyleProps
  imgProps?: StyleProps
  usePublicUrl?: boolean
  disableRemove?: boolean
}

export function MediaSelectPreviewSelectedImagesArea(
  props: {
    group?: ImageItemListGroup
    onRemove?: (itemId: string) => void
  } & Omit<ImageItemProps, 'itemId' | 'isUploading' | 'onRemove'>,
) {
  const { onRemove, group, ...rest } = props
  const [imageItems, setImageItems] = useImageItemList(group)

  const onRemoveCb = useCallback((itemId: string) => {
    setImageItems(prev => {
      const arr = [...prev]
      return arr.filter(i => i !== itemId)
    })
    onRemove?.(itemId)
  }, [])

  if (!imageItems.length) return null

  return (
    <>
      {imageItems.map(itemId => (
        <SelectedImageItem
          key={itemId}
          isUploading={itemId.startsWith('upload')}
          itemId={itemId}
          onRemove={onRemoveCb}
          {...rest}
        />
      ))}
    </>
  )
}

function SelectedImageItem(props: ImageItemProps) {
  const uploadingImagesRef = useContext(MediaSelectPreviewPanelItemsRefContext)
  const [uploadProgress] = useContext(
    MediaSelectPreviewPanelItemsProgressContext,
  )
  const { itemId, isUploading } = props
  const { data: mediaData } = useGetMedia(isUploading ? skipToken : itemId)

  const thumbnailUrl = getThumbnailUrl(mediaData)
  const publicUrl = getPublicUrl(mediaData)
  const record = isUploading
    ? uploadingImagesRef.current?.get(itemId)
    : undefined
  const objUrl = useObjectUrl(record?.file)

  const mediaUrl = (props.usePublicUrl ? publicUrl : thumbnailUrl) ?? objUrl

  const progress = (uploadProgress[itemId] ?? 0) * 100

  return (
    <div
      className={cx('relative group', props.wrapperProps?.className)}
      style={props.wrapperProps?.style}
    >
      {mediaUrl && (
        <img
          className={props.imgProps?.className}
          style={props.imgProps?.style}
          src={mediaUrl}
          loading="lazy"
          decoding="async"
        />
      )}
      {isUploading && (
        <div className="absolute top-0 left-0 w-full h-full backdrop-blur bg-white/40 dark:bg-black/40 flex items-center justify-center rounded-lg">
          <CircularProgress
            variant={progress > 15 ? 'determinate' : undefined}
            value={progress}
          />
        </div>
      )}
      {!props.disableRemove && (
        <button
          title="Remove"
          className={cx(
            'absolute right-1 top-1 bg-white text-black p-0 rounded-full',
            '[@media((hover:hover)_and_(pointer:fine))]:hidden group-hover:block',
          )}
          onClick={() => {
            if (isUploading) {
              record?.controller.abort()
            }
            props.onRemove(props.itemId)
          }}
        >
          <IcBaselineCancel className="size-5" />
        </button>
      )}
    </div>
  )
}
